// Shared definitions for the story tag filter system.
//
// v4 taxonomy: 6 filterable dimensions, in display order.
// Order here drives both the UI ordering and the URL-param keys.
//
// Used by:
//   - LandingPage hero search (filters live in local state, serialized
//     into /search URL on submit)
//   - SearchResultsPage (URL is source of truth)
//   - <SearchFilters /> reusable UI component
//
// Keep dimensions sourced from `km_tags` in sync with km_tags.dimension values
// in Supabase and the corresponding columns on km_stories.
//
// `source` controls where options come from at runtime:
//   - 'supabase' (default) → fetched via storyRepo.listTags(dimension)
//   - 'static'             → read from a local module (see useTagOptions)
//
// Practices use 'static' because they're not stored in km_tags yet — they'll
// move into a shared `sadhanas` table that both Aaptha Mithra and Katha
// Mithra reference. Until then, frontend/src/lib/practices.js is the source.

// `emoji` is the single source of truth for the per-category icon used
// across surfaces (hero typeahead row, filter-panel section heading,
// possibly the story page later). One emoji per dimension — change it
// here and every surface updates.
export const FILTER_DIMENSIONS = [
  { key: 'professions',     param: 'pf', label: 'Profession',    dimension: 'profession',    source: 'supabase', emoji: '💼' },
  { key: 'life_areas',      param: 'la', label: 'Life area',     dimension: 'life_area',     source: 'supabase', emoji: '🏠' },
  { key: 'guna_arc',        param: 'gn', label: 'Energy arc',    dimension: 'guna',          source: 'supabase', emoji: '🌅' },
  { key: 'inner_qualities', param: 'iq', label: 'What shifted',  dimension: 'inner_quality', source: 'supabase', emoji: '✨' },
  { key: 'inner_knots',     param: 'ik', label: 'Inner knot',    dimension: 'inner_knot',    source: 'supabase', emoji: '🪢' },
  { key: 'obstacles',       param: 'ob', label: 'Obstacle',      dimension: 'obstacle',      source: 'supabase', emoji: '🚧' },
  { key: 'mental_states',   param: 'ms', label: 'Mental state',  dimension: 'mental_state',  source: 'supabase', emoji: '💭' },
  { key: 'practice_ids',    param: 'pr', label: 'Practice',      dimension: 'practice',      source: 'static',   emoji: '🧘' },
];

// URL <-> Set helpers. Comma-separated representation (e.g. ?la=career,health)
// stays bookmarkable and shareable.
export const paramsToSet = (raw) => new Set((raw || '').split(',').filter(Boolean));
export const setToParam  = (set) => Array.from(set).join(',');

// Build the per-dimension selection map from URLSearchParams.
export function selectedFromParams(searchParams) {
  return FILTER_DIMENSIONS.reduce((acc, { key, param }) => {
    acc[key] = paramsToSet(searchParams.get(param));
    return acc;
  }, {});
}

// Empty selection — used as initial state on pages that hold filters
// in component state (e.g. LandingPage).
export function emptySelection() {
  return FILTER_DIMENSIONS.reduce((acc, { key }) => {
    acc[key] = new Set();
    return acc;
  }, {});
}

// Total number of selected tags across all dimensions.
export function totalSelectedCount(selected) {
  return FILTER_DIMENSIONS.reduce((sum, { key }) => sum + (selected[key]?.size || 0), 0);
}

// Build a per-dimension array payload suitable for the search/list APIs.
// Empty dimensions omitted so the backend treats them as "no constraint".
export function selectedToFiltersPayload(selected) {
  const filters = {};
  for (const { key } of FILTER_DIMENSIONS) {
    if (selected[key]?.size) filters[key] = Array.from(selected[key]);
  }
  return filters;
}

// Serialize selection into URLSearchParams (clones, mutates, returns).
export function applySelectionToParams(searchParams, selected) {
  const next = new URLSearchParams(searchParams);
  for (const { key, param } of FILTER_DIMENSIONS) {
    const set = selected[key];
    if (set && set.size) next.set(param, setToParam(set));
    else next.delete(param);
  }
  return next;
}
